import React from 'react';
import { Building2, Shield, Banknote } from 'lucide-react';
import { FeatureBadges } from '@/components/MicroTrustBadges';
import {
  PADMORA_REGISTRATION_BADGE_LABEL,
  PADMORA_REGISTRATION_BADGE_TOOLTIP,
} from '@/lib/constants';

export type TradingTrustBadge = {
  icon?: React.ReactNode;
  label: string;
  tooltip?: string;
};

type TradingTrustStripProps = {
  /** English (default) or Spanish registration badge copy. */
  locale?: 'en' | 'es';
  /** Extra badges appended after the standard LLC + ICC (+ optional banking) badges. */
  additionalBadges?: TradingTrustBadge[];
  /** Show US banking relationships badge (replaces former Singapore banking badge). */
  showBanking?: boolean;
  className?: string;
};

const REGISTRATION_ES = {
  label: 'LLC registrada en EE.UU. (EIN 32-0708681)',
  tooltip: 'Padmora LLC, registrada en Wyoming, EE.UU.',
};

const ICC_BADGE = {
  label: 'ICC UCP600 / ISP98 Compliant',
  tooltip: 'International Chamber of Commerce rules for documentary credits and standby guarantees',
};

const BANKING_BADGE = {
  label: 'US Banking Relationships',
  tooltip: 'Banking relationships with US institutions (details shared post-KYC under NDA)',
};

/**
 * Standard trust strip for trading/marketing pages: US LLC registration (with EIN) + ICC compliance.
 */
export function TradingTrustStrip({
  locale = 'en',
  additionalBadges = [],
  showBanking = true,
  className = 'mt-12 max-w-6xl mx-auto',
}: TradingTrustStripProps) {
  const registration =
    locale === 'es'
      ? REGISTRATION_ES
      : { label: PADMORA_REGISTRATION_BADGE_LABEL, tooltip: PADMORA_REGISTRATION_BADGE_TOOLTIP };

  const badges: TradingTrustBadge[] = [
    { icon: <Building2 className="h-4 w-4" />, ...registration },
    { icon: <Shield className="h-4 w-4" />, ...ICC_BADGE },
    ...(showBanking ? [{ icon: <Banknote className="h-4 w-4" />, ...BANKING_BADGE }] : []),
    ...additionalBadges,
  ];

  const colClass =
    badges.length >= 4
      ? 'lg:grid-cols-4'
      : badges.length === 3
        ? 'lg:grid-cols-3'
        : 'lg:grid-cols-2';

  return (
    <div className={className}>
      <div className={`grid grid-cols-1 md:grid-cols-2 ${colClass} gap-4 place-items-center md:place-items-start`}>
        {badges.map((badge) => (
          <FeatureBadges
            key={badge.label}
            features={[badge]}
            align="center"
            variant="trust"
          />
        ))}
      </div>
    </div>
  );
}
